import type { ContentCategory } from "@hexling/schema";

export interface CategoryInfo {
  category: ContentCategory;
  label: string;
  singular: string;
  glyph: string;
  blurb: string;
  group: "apothecary" | "sky" | "reference";
}

/** Library shelves, in the order they appear. Lessons live in their own tab. */
export const CATEGORIES: CategoryInfo[] = [
  {
    category: "plant",
    label: "Herbs & plants",
    singular: "plant",
    glyph: "🌿",
    blurb: "Correspondences, folklore and pet safety",
    group: "apothecary",
  },
  {
    category: "crystal",
    label: "Crystals",
    singular: "crystal",
    glyph: "💎",
    blurb: "Properties and how to care for each stone",
    group: "apothecary",
  },
  {
    category: "oil",
    label: "Oils",
    singular: "oil",
    glyph: "🫙",
    blurb: "Essential and carrier oils, with dilution guidance",
    group: "apothecary",
  },
  {
    category: "recipe",
    label: "Ritual recipes",
    singular: "recipe",
    glyph: "🧪",
    blurb: "Incense, teas, baths, jars and kitchen magic",
    group: "apothecary",
  },
  {
    category: "tarot_card",
    label: "Tarot",
    singular: "card",
    glyph: "🃏",
    blurb: "All 78 cards, upright and reversed",
    group: "sky",
  },
  {
    category: "sabbat",
    label: "Wheel of the year",
    singular: "sabbat",
    glyph: "☀️",
    blurb: "The eight sabbats and how to celebrate them",
    group: "sky",
  },
  {
    category: "moon_phase",
    label: "Moon phases",
    singular: "phase",
    glyph: "🌙",
    blurb: "What each phase is good for",
    group: "sky",
  },
  {
    category: "zodiac_sign",
    label: "Zodiac signs",
    singular: "sign",
    glyph: "♈",
    blurb: "Elements, modalities and rulers",
    group: "sky",
  },
  {
    category: "planet",
    label: "Planets",
    singular: "planet",
    glyph: "🪐",
    blurb: "Planetary energies, days and metals",
    group: "sky",
  },
  {
    category: "house",
    label: "Houses",
    singular: "house",
    glyph: "🏛️",
    blurb: "The twelve areas of life in a chart",
    group: "sky",
  },
  {
    category: "aspect",
    label: "Aspects",
    singular: "aspect",
    glyph: "📐",
    blurb: "How planets talk to each other",
    group: "sky",
  },
  {
    category: "element",
    label: "Elements",
    singular: "element",
    glyph: "🜁",
    blurb: "Earth, air, fire, water and spirit",
    group: "reference",
  },
  {
    category: "color",
    label: "Colors",
    singular: "color",
    glyph: "🎨",
    blurb: "Color magic for candles and cords",
    group: "reference",
  },
  {
    category: "day_of_week",
    label: "Days of the week",
    singular: "day",
    glyph: "🗓️",
    blurb: "Planetary days and what they favor",
    group: "reference",
  },
  {
    category: "chakra",
    label: "Chakras",
    singular: "chakra",
    glyph: "🔆",
    blurb: "The seven energy centers",
    group: "reference",
  },
  {
    category: "intent",
    label: "Intentions",
    singular: "intention",
    glyph: "✨",
    blurb: "Protection, love, prosperity and more",
    group: "reference",
  },
];

export const GROUP_LABELS: Record<CategoryInfo["group"], string> = {
  apothecary: "The apothecary",
  sky: "Sky & seasons",
  reference: "Correspondences",
};

export function categoryInfo(category: string): CategoryInfo | undefined {
  return CATEGORIES.find((c) => c.category === category);
}
